import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toolbar, Select, Pagination } from 'react-unity';
import './Paginator.css';
import PageResult from '../../../models/entities/PageResult';

interface PaginatorProps<T> {
	data: T[];
	totalItems?: number;
	itemsPerPage?: number;
	onPageChange: (rows: T[]) => any;
	pageResult?: PageResult;
	controller?: any;
	handleNewPage?: () => Promise<void> | void;
}

const Paginator = <T,>({ data, itemsPerPage, onPageChange, pageResult, handleNewPage }: PaginatorProps<T>) => {
	const defaultItemsPerPage = 25;

	const [itemsInPage, setItemsPerPage] = useState<number>(itemsPerPage || defaultItemsPerPage);
	const [pages, setPages] = useState<Record<string, T[]>>({ '1': [] });
	const [activePage, setActivePage] = useState<string>('1');
	const [pageGroup, setPageGroup] = useState<number>(1);
	const [amountOfPagesAvailable] = useState<number>(3);

	const onPageChangeRef = useRef(onPageChange);
	useEffect(() => {
		onPageChangeRef.current = onPageChange;
	}, [onPageChange]);

	const getPages = useCallback(
		(ipp: number) => {
			const dict: Record<string, T[]> = {};
			const total = Math.ceil((data?.length ?? 0) / ipp);
			for (let i = 0; i < total; i++) {
				const start = i * ipp;
				dict[String(i + 1)] = data.slice(start, start + ipp);
			}
			return dict;
		},
		[data]
	);

	const pageKeys = () => Object.keys(pages);

	useEffect(() => {
		const initialPages = getPages(itemsInPage);
		const first = Object.keys(initialPages)[0] ?? '1';
		setPages(initialPages);
		setActivePage(first);
		setPageGroup(1);
		onPageChangeRef.current(initialPages[first] ?? []);
	}, []);

	useEffect(() => {
		const newPages = getPages(itemsInPage);
		const keys = Object.keys(newPages);
		const last = keys.length ? keys[keys.length - 1] : '1';

		const nextActive = newPages[activePage] ? activePage : last;
		const nextGroup = Math.ceil(parseInt(nextActive, 10) / amountOfPagesAvailable) || 1;

		setPages(newPages);
		setActivePage(nextActive);
		setPageGroup(nextGroup);
		onPageChangeRef.current(newPages[nextActive] ?? []);
	}, [data]);

	const handleItemsPerPageChange = (numberOfItems: number) => {
		const newPages = getPages(numberOfItems);
		const first = Object.keys(newPages)[0] ?? '1';

		setItemsPerPage(numberOfItems);
		setPages(newPages);
		setActivePage(first);
		setPageGroup(1);
		onPageChangeRef.current(newPages[first] ?? []);
	};

	const goToPage = (pageIndex: string) => {
		setActivePage(pageIndex);
		onPageChangeRef.current(pages[pageIndex] ?? []);
	};

	const goToFirstPage = () => {
		setActivePage('1');
		setPageGroup(1);
		onPageChangeRef.current(pages['1'] ?? []);
	};

	const goToLastPage = async () => {
		const isAtLast = activePage === pageKeys().length.toString();

		if (isAtLast && pageResult?.hasNextPage && handleNewPage) {
			await Promise.resolve(handleNewPage()); // soporta void o Promise
		}

		const newPages = getPages(itemsInPage);
		const keys = Object.keys(newPages);
		const last = keys.length ? keys[keys.length - 1] : '1';

		setPages(newPages);
		setActivePage(last);
		setPageGroup(Math.ceil(keys.length / amountOfPagesAvailable) || 1);
		onPageChangeRef.current(newPages[last] ?? []);
	};

	const showThreeNextPages = useCallback(() => {
		setPageGroup((prevGroup) => {
			const newGroup = prevGroup + 1;

			const min = (newGroup - 1) * amountOfPagesAvailable + 1;
			const max = newGroup * amountOfPagesAvailable;

			const keys = Object.keys(pages)
				.map((k) => parseInt(k, 10))
				.sort((a, b) => a - b);

			const firstInRange = keys.find((k) => k >= min && k <= max);
			const target = (firstInRange ?? keys[keys.length - 1] ?? 1).toString();

			setActivePage(target);
			onPageChangeRef.current(pages[target] ?? []);

			return newGroup;
		});
	}, [amountOfPagesAvailable, pages]);

	const showThreePreviousPages = useCallback(() => {
		setPageGroup((prevGroup) => {
			const newGroup = Math.max(1, prevGroup - 1);

			const min = (newGroup - 1) * amountOfPagesAvailable + 1;
			const max = newGroup * amountOfPagesAvailable;

			const keys = Object.keys(pages)
				.map((k) => parseInt(k, 10))
				.sort((a, b) => a - b);

			const firstInRange = keys.find((k) => k >= min && k <= max);
			const target = (firstInRange ?? keys[0] ?? 1).toString();

			setActivePage(target);
			onPageChangeRef.current(pages[target] ?? []);

			return newGroup;
		});
	}, [amountOfPagesAvailable, pages]);

	return (
		<Toolbar>
			<Toolbar.Item>
				{data?.length} item{data?.length !== 1 && 's'}
			</Toolbar.Item>

			<Toolbar.Item>
				<Select
					value={itemsInPage}
					className="em-u-margin-bottom-none"
					size="small"
					onChange={(e: any) => {
						const value = Number(e.target.value);
						handleItemsPerPageChange(value);
					}}
				>
					{[10, 25, 50, 100].map((x) => (
						<option className="dropdown-paginator-filter-color" key={x} value={x}>
							{x} items per page
						</option>
					))}
				</Select>
			</Toolbar.Item>

			<Toolbar.Item right>
				{pageKeys().length > 0 && (
					<Pagination maxPages={amountOfPagesAvailable} initialPage={1}>
						<Pagination.First onChange={goToFirstPage} disabled={activePage === '1'} />

						{pageGroup > 1 && <Pagination.Item onChange={showThreePreviousPages}>...</Pagination.Item>}

						{pageKeys()
							.filter((key) => {
								const k = parseInt(key, 10);
								const min = pageGroup * amountOfPagesAvailable - (amountOfPagesAvailable - 1);
								const max = pageGroup * amountOfPagesAvailable;
								return k >= min && k <= max;
							})
							.map((key) => (
								<Pagination.Item
									onChange={() => goToPage(key)}
									key={key}
									active={activePage.toString() === key}
								>
									{key}
								</Pagination.Item>
							))}

						{pageKeys().length > pageGroup * amountOfPagesAvailable && (
							<Pagination.Item onChange={showThreeNextPages}>...</Pagination.Item>
						)}

						<Pagination.Last
							onChange={goToLastPage}
							disabled={activePage === pageKeys().length.toString() && !pageResult?.hasNextPage}
						/>
					</Pagination>
				)}
			</Toolbar.Item>
		</Toolbar>
	);
};

export default Paginator;
